import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import Select from "react-select";
import {
  fetchTenantEmailSettings,
  updateTenantEmailSettings,
} from "../../Redux/ActionCreators/settingAction";
import { useAppPermission } from "../../hooks/useAppPermission";
import { P } from "../../constants/rbac";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 8,
    fontSize: 14,
    fontFamily: '"inter", sans-serif',
    borderColor: state.isFocused ? "#f75891" : "#e4e9f0",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(247, 88, 145, 0.12)" : "none",
    "&:hover": { borderColor: state.isFocused ? "#f75891" : "#f9a8d4" },
  }),
  placeholder: (base) => ({ ...base, color: "#8a9199" }),
  singleValue: (base) => ({ ...base, color: "#111928" }),
  multiValue: (base) => ({ ...base, backgroundColor: "#fce7f1", borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: "#111928", fontSize: 13 }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#f75891",
    "&:hover": { backgroundColor: "#f9a8d4", color: "#fff" },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(17, 25, 40, 0.12)",
  }),
  option: (base, state) => ({
    ...base,
    padding: "8px 12px",
    cursor: "pointer",
    fontFamily: '"inter", sans-serif',
    backgroundColor: state.isSelected ? "#fce7f1" : state.isFocused ? "#fff7fb" : "#fff",
    color: "#111928",
  }),
};

const toOption = (u) => ({
  value: u.id,
  label: u.name ? `${u.name} (${u.email})` : u.email,
  email: u.email,
  name: u.name,
});

/** Company Profile tab: Reply-To + CC defaults for candidate emails. */
const EmailSettings = () => {
  const dispatch = useDispatch();
  const { can } = useAppPermission();
  const canEdit = can(P.SETTINGS_PROFILE_EDIT);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [ccUsers, setCcUsers] = useState([]);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const userOptions = useMemo(() => users.map(toOption), [users]);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await dispatch(fetchTenantEmailSettings());
    if (res.success) {
      const list = Array.isArray(res.data?.available_users) ? res.data.available_users : [];
      setUsers(list);
      const byId = Object.fromEntries(list.map((u) => [u.id, u]));
      const replyId = res.data?.reply_to_user_id;
      setReplyTo(replyId && byId[replyId] ? toOption(byId[replyId]) : null);
      const ccIds = Array.isArray(res.data?.cc_user_ids) ? res.data.cc_user_ids : [];
      setCcUsers(ccIds.map((id) => byId[id]).filter(Boolean).map(toOption));
    } else {
      setSnack({ open: true, message: res.message || "Failed to load", severity: "error" });
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    const res = await dispatch(
      updateTenantEmailSettings({
        reply_to_user_id: replyTo?.value ?? null,
        cc_user_ids: ccUsers.map((o) => o.value),
      })
    );
    setSaving(false);
    if (res.success) {
      setSnack({ open: true, message: "Email settings saved", severity: "success" });
      const list = Array.isArray(res.data?.available_users) ? res.data.available_users : users;
      setUsers(list);
    } else {
      setSnack({
        open: true,
        message: res.message || "Failed to save email settings",
        severity: "error",
      });
    }
  };

  return (
    <div className="account-holder">
      <h3>Email Settings</h3>
      <p style={{ fontSize: 13, color: "#525b66", margin: "8px 0 20px", maxWidth: 640 }}>
        Used for candidate emails when a job does not set its own Reply-To or CC.
        Clear and save to remove company defaults (no Reply-To / CC).
        Assigned recruiters (Round Robin or recruiter apply link) are always added to CC as well.
      </p>

      {loading ? (
        <div
          className="loading-overlay"
          style={{ position: "relative", height: 40, alignItems: "start" }}
        >
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div style={{ maxWidth: 560 }}>
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label style={{ fontWeight: 600, marginBottom: 8, display: "block" }}>Reply-To</label>
            <Select
              isClearable
              isDisabled={!canEdit}
              options={userOptions}
              value={replyTo}
              onChange={setReplyTo}
              placeholder="Select a user…"
              styles={selectStyles}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 24 }}>
            <label style={{ fontWeight: 600, marginBottom: 8, display: "block" }}>CC</label>
            <Select
              isMulti
              isClearable
              isDisabled={!canEdit}
              options={userOptions}
              value={ccUsers}
              onChange={(v) => setCcUsers(v || [])}
              placeholder="Select users to CC…"
              closeMenuOnSelect={false}
              styles={selectStyles}
            />
          </div>

          {canEdit && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{
                border: "none",
                backgroundColor: "#f75891",
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
                borderRadius: 4,
                height: 36,
                padding: "0 20px",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EmailSettings;
